import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Link,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import BLOG_ARTICLES from "../../../content/blog/articles";

const TOPICS = [
  {
    title: "Screen AI & online learning",
    body: "Capture lecture tabs, merge slides with audio, and ask questions from saved screenshots—without retyping every bullet.",
    slugs: [
      "screen-ai-lecture-notes-online-classes",
      "screen-ai-meeting-recap-without-typing",
      "ask-questions-about-past-screen-sessions",
    ],
  },
  {
    title: "Media AI for study & work",
    body: "Upload slides, PDFs, or video segments for explanations per frame—ideal for exam prep and async training.",
    slugs: [
      "media-ai-summarize-pdf-slides",
      "media-ai-video-study-guide",
      "working-professional-screen-and-media-ai",
    ],
  },
  {
    title: "Money, projects & daily tasks",
    body: "Track income and expenses, run Kanban projects, nested todos, and recurring chores—with optional voice when you have AI credits.",
    slugs: [
      "portfolio-ai-personal-finance-workspace",
      "voice-project-management-kanban",
      "todos-ai-nested-checklists",
      "daily-chore-ai-routine-reminders",
    ],
  },
  {
    title: "Shared life admin",
    body: "Calendar, notes, files, and Splitwise-style group bills in the same workspace you use for AI capture.",
    slugs: [
      "split-bills-roommates-splitwise-alternative",
      "personal-ai-assistant-everyday-life",
      "one-app-notes-calendar-files",
    ],
  },
];

function pickArticles(slugs) {
  return slugs
    .map((slug) => BLOG_ARTICLES.find((a) => a.slug === slug))
    .filter(Boolean);
}

export default function SeoContentSection() {
  const muted = useColorModeValue("gray.600", "gray.300");
  const deep = useColorModeValue("gray.900", "white");
  const cardBg = useColorModeValue("whiteAlpha.800", "gray.800");

  return (
    <Box as="section" py={{ base: 10, md: 14 }} aria-labelledby="problems-we-solve">
      <Container maxW="container.xl">
        <VStack align="stretch" spacing={8}>
          <Box>
            <Heading as="h2" id="problems-we-solve" size="lg" color={deep} mb={3}>
              Problems Manage360 solves
            </Heading>
            <Text color={muted} fontSize="md" lineHeight="tall" maxW="3xl">
              One free dashboard for students, professionals, teachers, and
              households—optional Screen AI, Media AI, and voice on money and
              tasks when you buy AI Usage Credits. Explore guides below or{" "}
              <Link as={RouterLink} to="/blog" color="teal.500" fontWeight="medium">
                browse all {BLOG_ARTICLES.length} articles
              </Link>
              .
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {TOPICS.map((topic) => {
              const links = pickArticles(topic.slugs);
              return (
                <Box
                  key={topic.title}
                  bg={cardBg}
                  borderRadius="xl"
                  borderWidth="1px"
                  p={6}
                >
                  <Heading as="h3" size="md" color={deep} mb={2}>
                    {topic.title}
                  </Heading>
                  <Text color={muted} fontSize="sm" lineHeight="tall" mb={4}>
                    {topic.body}
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {links.map((a) => (
                      <Link
                        key={a.slug}
                        as={RouterLink}
                        to={`/blog/${a.slug}`}
                        fontSize="sm"
                        color="teal.500"
                      >
                        {a.title}
                      </Link>
                    ))}
                  </VStack>
                </Box>
              );
            })}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
}
